import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import {
  FileSpreadsheet, FileText, FolderKanban, MessageSquareWarning, Ticket,
  Users2, Timer, Target, AlertTriangle, TrendingUp, Award, Loader2, RefreshCw,
} from "lucide-react";

const PALETTE = ["#1E3A8A", "#3B82F6", "#8B5CF6", "#F97316", "#EF4444", "#10B981", "#0EA5E9", "#F59E0B", "#EC4899"];

function KpiCard({ icon: Icon, label, value, hint, tone = "blue", testid }) {
  const tones = {
    blue: "bg-blue-100 text-blue-800",
    orange: "bg-orange-100 text-orange-700",
    red: "bg-red-100 text-red-700",
    emerald: "bg-emerald-100 text-emerald-700",
    violet: "bg-violet-100 text-violet-700",
    slate: "bg-slate-100 text-slate-700",
  };
  return (
    <Card className="border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 card-hover" data-testid={testid}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div className="min-w-0">
            <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">{label}</div>
            <div className="mt-2 font-display text-3xl font-bold text-slate-900 dark:text-slate-100 tracking-tight">
              {value ?? "—"}
            </div>
            {hint && <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">{hint}</div>}
          </div>
          <div className={`w-10 h-10 rounded-lg grid place-items-center shrink-0 ${tones[tone]}`}>
            <Icon className="w-5 h-5" strokeWidth={1.75} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function SectionCard({ title, subtitle, children, testid, action }) {
  return (
    <Card className="border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900" data-testid={testid}>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <div>
            {subtitle && (
              <div className="text-[10px] uppercase tracking-[0.28em] text-slate-400 dark:text-slate-500">{subtitle}</div>
            )}
            <CardTitle className="font-display text-xl">{title}</CardTitle>
          </div>
          {action}
        </div>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

function labelize(v) {
  if (!v) return "—";
  const map = {
    // dossier statuts
    enregistrement: "Enregistrement",
    affectation: "Affectation",
    traitement: "En traitement",
    validation: "Validation",
    cloture: "Clôturé",
    rejete: "Rejeté",
    // réclamation statuts
    nouvelle: "Nouvelle",
    assignee: "Assignée",
    en_traitement: "En traitement",
    en_attente_client: "En attente client",
    validee: "Validée",
    resolue: "Résolue",
    fermee: "Fermée",
    rejetee: "Rejetée",
    // priorités
    critique: "Critique",
    haute: "Haute",
    normale: "Normale",
    faible: "Faible",
    // catégories / canaux
    pension: "Pension",
    cotisation: "Cotisation",
    prestations: "Prestations",
    carte: "Carte",
    employeur: "Employeur",
    technique: "Technique",
    autre: "Autre",
    telephone: "Téléphone",
    email: "E-mail",
    courrier: "Courrier",
    accueil: "Accueil",
    web: "Web",
    whatsapp: "WhatsApp",
    reseaux_sociaux: "Réseaux sociaux",
  };
  return map[v] || v;
}

function downloadBlob(blob, filename) {
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(link.href);
}

export default function Analytics() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(null); // "xlsx" | "pdf" | null

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/analytics/synthesis");
      setData(res.data);
    } catch (e) {
      console.error(e);
      toast.error("Impossible de charger les analytics", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const exportFile = async (format) => {
    setExporting(format);
    try {
      const path = format === "pdf" ? "/analytics/rapport.pdf" : "/analytics/rapport.xlsx";
      const res = await api.get(path, { responseType: "blob" });
      const mime = format === "pdf"
        ? "application/pdf"
        : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const stamp = new Date().toISOString().slice(0, 16).replace(/[-:T]/g, "");
      const filename = `analytics_dkconnect_${stamp}.${format}`;
      downloadBlob(new Blob([res.data], { type: mime }), filename);
      toast.success(`Rapport ${format.toUpperCase()} téléchargé`);
    } catch (e) {
      console.error(e);
      toast.error("Export impossible", { description: e?.response?.data?.detail || e.message });
    } finally {
      setExporting(null);
    }
  };

  const dossiersParStatut = useMemo(
    () => (data?.dossiers?.par_statut || []).map((r) => ({ ...r, label: labelize(r.label) })),
    [data],
  );
  const dossiersParPriorite = useMemo(
    () => (data?.dossiers?.par_priorite || []).map((r) => ({ ...r, label: labelize(r.label) })),
    [data],
  );
  const recParStatut = useMemo(
    () => (data?.reclamations?.par_statut || []).map((r) => ({ ...r, label: labelize(r.label) })),
    [data],
  );
  const recParPriorite = useMemo(
    () => (data?.reclamations?.par_priorite || []).map((r) => ({ ...r, label: labelize(r.label) })),
    [data],
  );
  const recParCategorie = useMemo(
    () => (data?.reclamations?.par_categorie || []).map((r) => ({ ...r, label: labelize(r.label) })),
    [data],
  );
  const recParCanal = useMemo(
    () => (data?.reclamations?.par_canal || []).map((r) => ({ ...r, label: labelize(r.label) })),
    [data],
  );

  const d = data?.dossiers;
  const r = data?.reclamations;
  const tk = data?.tickets;
  const res = data?.ressources;

  return (
    <div>
      <PageHeader
        eyebrow="Administration"
        title="Analytics & synthèse KPI"
        description="Vue globale de la performance opérationnelle : dossiers, réclamations, tickets et agents."
        testid="analytics-header"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="outline"
              className="rounded-full"
              onClick={load}
              disabled={loading}
              data-testid="analytics-refresh-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Rafraîchir
            </Button>
            <Button
              variant="outline"
              className="rounded-full"
              onClick={() => exportFile("xlsx")}
              disabled={exporting !== null}
              data-testid="analytics-export-xlsx-btn"
            >
              {exporting === "xlsx"
                ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                : <FileSpreadsheet className="w-4 h-4 mr-2" />}
              Export Excel
            </Button>
            <Button
              className="rounded-full bg-[#0B1B4B] hover:bg-[#08154A] text-white"
              onClick={() => exportFile("pdf")}
              disabled={exporting !== null}
              data-testid="analytics-export-pdf-btn"
            >
              {exporting === "pdf"
                ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                : <FileText className="w-4 h-4 mr-2" />}
              Export PDF
            </Button>
          </div>
        }
      />

      {loading && !data ? (
        <div className="h-64 grid place-items-center text-slate-500">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : !data ? (
        <div className="h-64 grid place-items-center text-slate-500">Aucune donnée</div>
      ) : (
        <>
          {/* Top KPIs */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="analytics-kpi-grid">
            <KpiCard
              icon={FolderKanban}
              label="Dossiers ouverts"
              value={d.ouverts}
              hint={`${d.total} au total · ${d.en_retard} en retard`}
              tone="blue"
              testid="kpi-dossiers-ouverts"
            />
            <KpiCard
              icon={MessageSquareWarning}
              label="Réclamations actives"
              value={r.ouvertes}
              hint={`${r.total} historiques · ${r.critiques_ouvertes} critiques`}
              tone="orange"
              testid="kpi-rec-ouvertes"
            />
            <KpiCard
              icon={Target}
              label="Taux de résolution"
              value={`${r.taux_resolution_pct}%`}
              hint={`${r.resolues} résolues sur ${r.total}`}
              tone="emerald"
              testid="kpi-taux-resolution"
            />
            <KpiCard
              icon={Timer}
              label="Temps moyen résolution"
              value={r.temps_moyen_resolution_h !== null ? `${r.temps_moyen_resolution_h} h` : "—"}
              hint="Créée → résolue"
              tone="violet"
              testid="kpi-temps-moyen"
            />
          </div>

          {/* Secondary row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <KpiCard
              icon={AlertTriangle}
              label="Réclamations en retard"
              value={r.en_retard}
              hint="SLA dépassé"
              tone="red"
              testid="kpi-rec-retard"
            />
            <KpiCard
              icon={TrendingUp}
              label="Respect SLA"
              value={`${r.taux_sla_respect_pct}%`}
              hint="Résolues avant échéance"
              tone="emerald"
              testid="kpi-sla-respect"
            />
            <KpiCard
              icon={Ticket}
              label="Tickets servis"
              value={tk.servis}
              hint={`${tk.total} tickets au total`}
              tone="blue"
              testid="kpi-tickets-servis"
            />
            <KpiCard
              icon={Users2}
              label="Clients & agents"
              value={`${res.clients} / ${res.agents}`}
              hint={`${res.utilisateurs} utilisateurs`}
              tone="slate"
              testid="kpi-ressources"
            />
          </div>

          {/* Charts row 1: Reclamations statut + priorité */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            <SectionCard title="Réclamations par statut" subtitle="Répartition" testid="section-rec-statut">
              <div className="h-72">
                {recParStatut.length === 0 ? (
                  <div className="h-full grid place-items-center text-slate-400 text-sm">Aucune donnée</div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={recParStatut} margin={{ left: -20, right: 12, top: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="label" tick={{ fill: "#64748B", fontSize: 11 }} interval={0} angle={-20} textAnchor="end" height={60} />
                      <YAxis allowDecimals={false} tick={{ fill: "#64748B", fontSize: 11 }} />
                      <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0" }} />
                      <Bar dataKey="count" fill="#1E3A8A" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </SectionCard>

            <SectionCard title="Réclamations par priorité" subtitle="SLA" testid="section-rec-priorite">
              <div className="h-72">
                {recParPriorite.length === 0 ? (
                  <div className="h-full grid place-items-center text-slate-400 text-sm">Aucune donnée</div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={recParPriorite}
                        dataKey="count"
                        nameKey="label"
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={90}
                        paddingAngle={2}
                      >
                        {recParPriorite.map((entry, i) => (
                          <Cell key={entry.label} fill={PALETTE[i % PALETTE.length]} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0" }} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </div>
            </SectionCard>
          </div>

          {/* Charts row 2: catégorie + canal */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            <SectionCard title="Réclamations par catégorie" subtitle="Nature" testid="section-rec-categorie">
              <div className="h-72">
                {recParCategorie.length === 0 ? (
                  <div className="h-full grid place-items-center text-slate-400 text-sm">Aucune donnée</div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={recParCategorie} margin={{ left: -20, right: 12, top: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="label" tick={{ fill: "#64748B", fontSize: 11 }} />
                      <YAxis allowDecimals={false} tick={{ fill: "#64748B", fontSize: 11 }} />
                      <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0" }} />
                      <Bar dataKey="count" fill="#8B5CF6" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </SectionCard>

            <SectionCard title="Réclamations par canal" subtitle="Origine" testid="section-rec-canal">
              <div className="h-72">
                {recParCanal.length === 0 ? (
                  <div className="h-full grid place-items-center text-slate-400 text-sm">Aucune donnée</div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={recParCanal} dataKey="count" nameKey="label" cx="50%" cy="50%" outerRadius={90}>
                        {recParCanal.map((entry, i) => (
                          <Cell key={entry.label} fill={PALETTE[(i + 2) % PALETTE.length]} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0" }} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </div>
            </SectionCard>
          </div>

          {/* Dossiers charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            <SectionCard title="Dossiers par statut" subtitle="Cycle de vie" testid="section-dos-statut">
              <div className="h-72">
                {dossiersParStatut.length === 0 ? (
                  <div className="h-full grid place-items-center text-slate-400 text-sm">Aucune donnée</div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={dossiersParStatut} margin={{ left: -20, right: 12, top: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="label" tick={{ fill: "#64748B", fontSize: 11 }} interval={0} angle={-20} textAnchor="end" height={60} />
                      <YAxis allowDecimals={false} tick={{ fill: "#64748B", fontSize: 11 }} />
                      <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0" }} />
                      <Bar dataKey="count" fill="#3B82F6" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </SectionCard>

            <SectionCard title="Dossiers par priorité" subtitle="Urgence" testid="section-dos-priorite">
              <div className="h-72">
                {dossiersParPriorite.length === 0 ? (
                  <div className="h-full grid place-items-center text-slate-400 text-sm">Aucune donnée</div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={dossiersParPriorite} dataKey="count" nameKey="label" cx="50%" cy="50%" outerRadius={90}>
                        {dossiersParPriorite.map((entry, i) => (
                          <Cell key={entry.label} fill={PALETTE[(i + 1) % PALETTE.length]} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0" }} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </div>
            </SectionCard>
          </div>

          {/* Tickets summary + Top agents */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
            <SectionCard title="File d'attente" subtitle="Tickets globaux" testid="section-tickets">
              <div className="space-y-3">
                <StatRow label="Servis" value={tk.servis} tone="emerald" />
                <StatRow label="En attente" value={tk.en_attente} tone="orange" />
                <StatRow label="En service" value={tk.en_service} tone="blue" />
                <StatRow label="Absents" value={tk.absents} tone="red" />
                <div className="pt-3 border-t border-slate-200 dark:border-slate-800 text-sm text-slate-600 dark:text-slate-300">
                  Temps d&apos;attente moyen :{" "}
                  <span className="font-semibold text-slate-900 dark:text-slate-100">
                    {tk.temps_attente_moyen_min !== null ? `${tk.temps_attente_moyen_min} min` : "—"}
                  </span>
                </div>
              </div>
            </SectionCard>

            <Card className="lg:col-span-2 border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900" data-testid="section-top-agents">
              <CardHeader>
                <div className="text-[10px] uppercase tracking-[0.28em] text-slate-400 dark:text-slate-500">Performance</div>
                <CardTitle className="font-display text-xl flex items-center gap-2">
                  <Award className="w-5 h-5 text-blue-800 dark:text-blue-300" />
                  Top agents — réclamations résolues
                </CardTitle>
              </CardHeader>
              <CardContent>
                {(!data.top_agents || data.top_agents.length === 0) ? (
                  <div className="h-40 grid place-items-center text-slate-400 text-sm">Aucun agent classé</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-[11px] uppercase tracking-widest text-slate-500 border-b border-slate-200 dark:border-slate-800">
                          <th className="py-2 font-medium">#</th>
                          <th className="py-2 font-medium">Agent</th>
                          <th className="py-2 font-medium">Service</th>
                          <th className="py-2 font-medium text-right">Résolues</th>
                          <th className="py-2 font-medium text-right">Temps moyen</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.top_agents.map((a, i) => (
                          <tr key={`${a.agent}-${i}`} className="border-b border-slate-100 dark:border-slate-800 last:border-0" data-testid={`top-agent-row-${i}`}>
                            <td className="py-2 text-slate-500">{i + 1}</td>
                            <td className="py-2 font-medium text-slate-800 dark:text-slate-100">{a.agent}</td>
                            <td className="py-2 text-slate-500">{a.service || "—"}</td>
                            <td className="py-2 text-right">
                              <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">{a.resolues}</Badge>
                            </td>
                            <td className="py-2 text-right font-mono text-slate-600 dark:text-slate-300">
                              {a.temps_moyen_h !== null && a.temps_moyen_h !== undefined ? `${a.temps_moyen_h} h` : "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="text-xs text-slate-400 dark:text-slate-500 mt-8">
            Rapport généré le {new Date(data.generated_at).toLocaleString("fr-FR")}
          </div>
        </>
      )}
    </div>
  );
}

function StatRow({ label, value, tone = "blue" }) {
  const tones = {
    blue: "text-blue-700",
    emerald: "text-emerald-700",
    orange: "text-orange-700",
    red: "text-red-700",
  };
  return (
    <div className="flex items-center justify-between">
      <div className="text-sm text-slate-600 dark:text-slate-300">{label}</div>
      <div className={`font-display text-2xl font-bold ${tones[tone]}`}>{value ?? 0}</div>
    </div>
  );
}
